import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import LazyImgixImage from '@components/LazyImgixImage';
import { WIDTH_MD, WIDTH_SM } from '@utils/constants';
import { getImgixSrc } from '@utils/images';

import { MIDDLE_SLIDE_SHOW_IMAGES } from './constant';

const AUTO_ADVANCE_MS = 5500;
const PROGRAMMATIC_SCROLL_CLEAR_MS = 650;

function getSlidesPerView(width: number): number {
  if (width >= WIDTH_MD) {
    return 3;
  }
  if (width >= WIDTH_SM) {
    return 2;
  }
  return 1;
}

const GlanceSlideshow = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [slideWidthPx, setSlideWidthPx] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const isProgrammaticScrollRef = useRef(false);
  const programmaticClearTimeoutRef = useRef<number>();

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);
    const onChange = () => setPrefersReducedMotion(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      const perView = getSlidesPerView(w);
      setSlideWidthPx(w / perView);
    };
    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    const total = MIDDLE_SLIDE_SHOW_IMAGES.length;
    if (!el || slideWidthPx <= 0 || total <= 1 || prefersReducedMotion) {
      return undefined;
    }

    let intervalId: number;

    const clearProgrammaticFlagSoon = () => {
      window.clearTimeout(programmaticClearTimeoutRef.current);
      programmaticClearTimeoutRef.current = window.setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, PROGRAMMATIC_SCROLL_CLEAR_MS);
    };

    const advance = () => {
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll <= 0) {
        return;
      }
      isProgrammaticScrollRef.current = true;
      const pageStep = el.clientWidth;
      const next = el.scrollLeft + pageStep;
      if (next > maxScroll + 0.5) {
        el.scrollTo({ left: 0, behavior: 'auto' });
      } else {
        const behavior: ScrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';
        el.scrollTo({ left: next, behavior });
      }
      clearProgrammaticFlagSoon();
    };

    const restartInterval = () => {
      window.clearInterval(intervalId);
      intervalId = window.setInterval(advance, AUTO_ADVANCE_MS);
    };

    const onScroll = () => {
      if (isProgrammaticScrollRef.current) {
        return;
      }
      restartInterval();
    };

    const onScrollEnd = () => {
      isProgrammaticScrollRef.current = false;
    };

    restartInterval();
    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('scrollend', onScrollEnd);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(programmaticClearTimeoutRef.current);
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('scrollend', onScrollEnd);
    };
  }, [slideWidthPx, prefersReducedMotion]);

  const trackWidthPx = slideWidthPx * MIDDLE_SLIDE_SHOW_IMAGES.length;

  return (
    <div className="mx-auto max-w-[1728px]">
      <div
        ref={containerRef}
        className="mb-112 w-full snap-x snap-mandatory overflow-x-auto overflow-y-hidden scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] motion-reduce:scroll-auto [&::-webkit-scrollbar]:hidden"
        role="region"
        aria-roledescription="carousel"
        aria-label="Event photos"
      >
        {slideWidthPx > 0 ? (
          <div className="flex gap-0" style={{ width: trackWidthPx }}>
            {MIDDLE_SLIDE_SHOW_IMAGES.map((path, index) => (
              <div key={path} className="shrink-0 snap-start" style={{ width: slideWidthPx }}>
                <LazyImgixImage
                  src={getImgixSrc({ path, isEnvDependent: false })}
                  alt=""
                  className="block h-[min(42vw,320px)] w-full object-cover object-center sm:h-[min(28vw,380px)] md:h-[min(22vw,420px)]"
                  sizes="(max-width: 767px) 100vw, (max-width: 991px) 50vw, 33vw"
                  visibleByDefault={index === 0}
                  wrapperClassName="!block h-full min-h-0 w-full leading-[0]"
                />
              </div>
            ))}
          </div>
        ) : (
          <div
            className="h-[min(42vw,320px)] w-full bg-grayscale-charcoal sm:h-[min(28vw,380px)] md:h-[min(22vw,420px)]"
            aria-hidden
          />
        )}
      </div>
    </div>
  );
};

export default GlanceSlideshow;
